/**
 * Tiny helper for authoring pixel art in code. Everything the game draws is generated
 * at start-up through this: no external image assets exist in the project.
 */
export class PixelBuf {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;

  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = w;
    this.canvas.height = h;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable');
    ctx.imageSmoothingEnabled = false;
    this.ctx = ctx;
  }

  clear(): this {
    this.ctx.clearRect(0, 0, this.w, this.h);
    return this;
  }

  px(x: number, y: number, color: string): this {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(x | 0, y | 0, 1, 1);
    return this;
  }

  rect(x: number, y: number, w: number, h: number, color: string): this {
    if (w <= 0 || h <= 0) return this;
    this.ctx.fillStyle = color;
    this.ctx.fillRect(x | 0, y | 0, w | 0, h | 0);
    return this;
  }

  /** Hollow rectangle, 1px border. */
  frame(x: number, y: number, w: number, h: number, color: string): this {
    this.rect(x, y, w, 1, color);
    this.rect(x, y + h - 1, w, 1, color);
    this.rect(x, y, 1, h, color);
    this.rect(x + w - 1, y, 1, h, color);
    return this;
  }

  /** Fill from a string map. '.' / ' ' are transparent, other chars index `palette`. */
  art(x: number, y: number, rows: readonly string[], palette: Record<string, string>): this {
    for (let ry = 0; ry < rows.length; ry++) {
      const row = rows[ry];
      for (let rx = 0; rx < row.length; rx++) {
        const ch = row[rx];
        if (ch === '.' || ch === ' ') continue;
        const c = palette[ch];
        if (c) this.px(x + rx, y + ry, c);
      }
    }
    return this;
  }

  /** Rounded-ish blob: rectangle with the four corner pixels removed. */
  blob(x: number, y: number, w: number, h: number, color: string): this {
    this.rect(x + 1, y, w - 2, h, color);
    this.rect(x, y + 1, w, h - 2, color);
    return this;
  }

  /** Horizontally mirrored copy. */
  flipped(): PixelBuf {
    const out = new PixelBuf(this.w, this.h);
    out.ctx.translate(this.w, 0);
    out.ctx.scale(-1, 1);
    out.ctx.drawImage(this.canvas, 0, 0);
    out.ctx.setTransform(1, 0, 0, 1, 0, 0);
    return out;
  }

  /** Flat colour copy preserving alpha (used for star-power flashes and silhouettes). */
  tinted(color: string, strength = 1): PixelBuf {
    const out = new PixelBuf(this.w, this.h);
    out.ctx.drawImage(this.canvas, 0, 0);
    out.ctx.globalAlpha = strength;
    out.ctx.globalCompositeOperation = 'source-atop';
    out.ctx.fillStyle = color;
    out.ctx.fillRect(0, 0, this.w, this.h);
    out.ctx.globalCompositeOperation = 'source-over';
    out.ctx.globalAlpha = 1;
    return out;
  }
}
